import React, { useState, useEffect, useRef } from 'react';
import PageContainer from 'src/components/container/PageContainer';
import DashboardCard2 from 'src/components/shared/DashboardCard2';
import ShowLoader from 'src/components/CustomComponents/ShowLoader';
import { Button, FormControl, MenuItem, Select } from '@mui/material';
import { useMain } from 'src/views/hooks/useMain';
import { useDispatch, useSelector } from 'react-redux';
import {
  setAppointmentAnalyticsData,
  setAppointmentAnalyticsLoading,
  setAppointmentAnalyticsFilters,
  setAppointmentAnalyticsError,
  resetAppointmentAnalyticsFilters
} from 'src/redux/slices/appointmentSlice';
import { changeDateFormate, changeTimeFormate } from 'src/utils/dateutils';
import { maskContact } from 'src/utils/maskUtils';

const filterOptions = [
  { label: 'Current Year', value: 'Current Year' },
  { label: 'Current Month', value: 'Current Month' },
  { label: 'Current Week (Sun-Sat)', value: 'Current Week' },
  { label: 'Today', value: 'Today' },
  { label: 'Custom Date', value: 'customDate' },
];

const AppointmentAnalytics = () => {
  const dispatch = useDispatch();
  const tableRef = useRef();
  const isInitialMount = useRef(true);

  const { fetchAppointmentAnalytics, activeBranchId,branchData } = useMain();
  const [updateMask, setUpdateMask] = useState(false);
    // Get data from Redux store
    const {
      data: tableData,
      filters: analyticsFilters,
      loading
    } = useSelector((state) => state.appointments.appointmentAnalytics);

    useEffect(() => {
      // Reset to default filters when component mounts
      dispatch(resetAppointmentAnalyticsFilters());
      
      return () => {
        // Clear data when component unmounts to prevent flash of old data
        dispatch(setAppointmentAnalyticsData([]));
      };
    }, []);
    useEffect(() => {
      setUpdateMask(!updateMask);
    }, [branchData?.isMasked]);

    useEffect(() => {
      if (!activeBranchId) return; // wait until branch id is actually loaded

      if (isInitialMount.current) {
        isInitialMount.current = false;
        getData();
        return;
      }

      // Skip API call for custom date until Apply is clicked
      if (analyticsFilters.timeRange !== 'customDate') {
        getData();
      }
    }, [activeBranchId, analyticsFilters.timeRange, analyticsFilters.staffFilter]);
  
    const getData = async () => {
      let queryParams = '';
  
      if (analyticsFilters.timeRange === 'customDate') {
        if (analyticsFilters.fromDate && analyticsFilters.toDate) {
          queryParams += `?startDate=${analyticsFilters.fromDate}&endDate=${analyticsFilters.toDate}`;
        } else {
          return; // Don't fetch if custom date but dates not selected
        }
      }
  
      if (analyticsFilters.staffFilter) {
        queryParams += queryParams ? `&staff=${analyticsFilters.staffFilter}` : `?staff=${analyticsFilters.staffFilter}`;
      }
  
      try {
        dispatch(setAppointmentAnalyticsLoading(true));
        const res = await fetchAppointmentAnalytics(activeBranchId, queryParams);
        
        if (res.statusCode === 200) {
          const filteredData = filterAppointments(res.data, analyticsFilters.timeRange);
          dispatch(setAppointmentAnalyticsData(filteredData));
        } else {
          dispatch(setAppointmentAnalyticsError('Failed to fetch data'));
        }
      } catch (error) {
        dispatch(setAppointmentAnalyticsError(error.message));
      }
    };
  
    const filterAppointments = (data, filterBy) => {
      return data.flatMap((item) =>
        item.appointments
          .filter((appointment) => {
            if (filterBy === 'Today') {
              const todayDate = new Date().toISOString().split('T')[0];
              return appointment.dateOfAppointment === todayDate;
            } else if (filterBy === 'Current Month') {
              const currentDate = new Date();
              const firstDayOfMonth = new Date(
                currentDate.getFullYear(),
                currentDate.getMonth(),
                1,
              )
                .toISOString()
                .split('T')[0];
              const lastDayOfMonth = new Date(
                currentDate.getFullYear(),
                currentDate.getMonth() + 1,
                0,
              )
                .toISOString()
                .split('T')[0];
              return (
                appointment.dateOfAppointment >= firstDayOfMonth &&
                appointment.dateOfAppointment <= lastDayOfMonth
              );
            } else if (filterBy === 'Current Week') {
              const currentDate = new Date();
              const firstDayOfWeek = new Date(
                currentDate.setDate(currentDate.getDate() - currentDate.getDay()),
              )
                .toISOString()
                .split('T')[0];
              const lastDayOfWeek = new Date(
                currentDate.setDate(currentDate.getDate() - currentDate.getDay() + 6),
              )
                .toISOString()
                .split('T')[0];
              return (
                appointment.dateOfAppointment >= firstDayOfWeek &&
                appointment.dateOfAppointment <= lastDayOfWeek
              );
            } else if (filterBy === 'Current Year') {
              const currentDate = new Date();
              const firstDayOfYear = new Date(currentDate.getFullYear(), 0, 1)
                .toISOString()
                .split('T')[0];
              const lastDayOfYear = new Date(currentDate.getFullYear(), 11, 31)
                .toISOString()
                .split('T')[0];
              return (
                appointment.dateOfAppointment >= firstDayOfYear &&
                appointment.dateOfAppointment <= lastDayOfYear
              );
            } else if (filterBy === 'customDate') {
              const startDate = new Date(analyticsFilters.fromDate).toISOString().split('T')[0];
              const endDate = new Date(analyticsFilters.toDate).toISOString().split('T')[0];
              return (
                appointment.dateOfAppointment >= startDate &&
                appointment.dateOfAppointment <= endDate
              );
            }
            return true;
          })
          .map((appointment) => ({
            ...appointment,
            staffName: item.staff.name,
          }))
      ) || [];
    };
  
    const handleFilterChange = (value) => {
      dispatch(setAppointmentAnalyticsFilters({ timeRange: value }));
    };
  
    const handleDateChange = (field, value) => {
      dispatch(setAppointmentAnalyticsFilters({ [field]: value }));
    };
  
    const handleApply = () => {
      if (analyticsFilters.fromDate && analyticsFilters.toDate) {
        getData();
      }
    }; 
    const analyticsGridTemplate = '1fr 1fr 1fr 1fr 0.7fr 1fr';
    const analyticsGridRowStyle = {
      display: 'grid',
      gridTemplateColumns: analyticsGridTemplate,
      alignItems: 'center',
      columnGap: '8px',
    };
  return (
    <PageContainer
      title="Appointment Analytics"
      description="This page contains Appointment Analytics"
    >
      <DashboardCard2 title="Appointment Analytics">
        {loading ? (
          <ShowLoader />
        ) : (
          <>
            <div className="buttons-container">
              <div className="row">
                <div className="col-3">
                  <FormControl fullWidth>
                    <div style={{ marginBottom: '3px', fontWeight: 'bold' }}>Filter</div>
                    <Select
                      name="sourceOfAppointment"
                      value={analyticsFilters.timeRange}
                      onChange={(e) => handleFilterChange(e.target.value)}
                      style={{width:'56%'}}
                    >
                      {filterOptions.map((sourceOption) => (
                        <MenuItem key={sourceOption.value} value={sourceOption.value}>
                          {sourceOption.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </div>
                {analyticsFilters.timeRange === 'customDate' && (
                  <>
                    <div className="col-2" style={{marginLeft:'-6.5rem'}}>
                      <div style={{ marginBottom: '3px', fontWeight: 'bold' }}>Start Date</div>
                      <input
                        type="date"
                        className="form-control"
                        style={{ padding: '16px' }}
                        name="from"
                        onChange={(e) => handleDateChange('fromDate', e.target.value)}
                        value={analyticsFilters.fromDate}
                      />
                    </div>
                    <div className="col-2">
                      <div style={{ marginBottom: '3px', fontWeight: 'bold' }}>End Date</div>
                      <input
                        type="date"
                        max={new Date().toISOString().split('T')[0]}
                        className="form-control"
                        style={{ padding: '16px' }}
                        name="to"
                        onChange={(e) => handleDateChange('toDate', e.target.value)}
                        value={analyticsFilters.toDate}
                      />
                    </div>
                    <div className="col-2" style={{ display: 'flex', alignItems: 'flex-end', width:'10%' }}>
                      <Button
                        disabled={!analyticsFilters.fromDate || !analyticsFilters.toDate}
                        variant="contained"
                        fullWidth
                        color="primary"
                        target="_blank"
                        sx={{ background: '#6174DD' }}
                        onClick={handleApply}
                      >
                        Apply
                      </Button>
                    </div>
                  </>
                )}
              </div>
            </div>
            {tableData.length !== 0 ? (
              <table className="table-container" ref={tableRef}>
                <thead className="table-thead">
                  <tr style={analyticsGridRowStyle}>
                    <th style={{ padding: '8px', textAlign: 'center' }}>Client Name</th>
                    <th style={{ padding: '8px', textAlign: 'center' }}>Client Number</th>
                    <th style={{ padding: '8px', textAlign: 'center' }}>Date of Appointment</th>
                    <th style={{ padding: '8px', textAlign: 'center' }}>Time of Appointment</th>
                    <th style={{ padding: '8px', textAlign: 'center' }}>Gender</th>
                    <th style={{ padding: '8px', textAlign: 'center' }}>Staff Name</th>
                  </tr>
                </thead>
                <tbody className="table-tbody">
                    {tableData.map((item) => {
                    const originalDate = item?.dateOfAppointment;
                      const originalTime = item?.timeOfAppointment;
                    // Convert "YYYY-MM-DD" to "DD-MM-YYYY"
                    // let formattedAppointmentDate = '';
                    // if (originalDate) {
                    //   const [year, month, day] = originalDate.split('-');
                    //   formattedAppointmentDate = `${day}-${month}-${year}`;
                    //   }
                      // let formattedAppointmentTime='';
                      // if (item?.timeOfAppointment) {
                      //   let [hours, minutes] = item?.timeOfAppointment.split(':').map(Number);
                      //   const postfix = hours >= 12 ? 'PM' : 'AM';
                      //   hours = hours % 12 || 12; // convert 0 or 12 to 12
                      
                      //   formattedAppointmentTime = `${hours}:${minutes
                      //     .toString()
                      //     .padStart(2, '0')} ${postfix}`;
                      // }
                      let formattedAppointmentDate = changeDateFormate(originalDate);
                      let formattedAppointmentTime = changeTimeFormate(originalTime);
                      // let formattedAppointmentTime = '';

                      // if (item?.timeOfAppointment) {
                      //   // Check if format matches HH:MM where HH = 00-23, MM = 00-59
                      //   const timePattern = /^([01]?\d|2[0-3]):([0-5]\d)$/;

                      //   const match = item.timeOfAppointment.match(timePattern);

                      //   if (match) {
                      //     let [hours, minutes] = match[0].split(':').map(Number);
                      //     const postfix = hours >= 12 ? 'PM' : 'AM';
                      //     hours = hours % 12 || 12;

                      //     formattedAppointmentTime = `${hours}:${minutes
                      //       .toString()
                      //       .padStart(2, '0')} ${postfix}`;
                      //   } else {
                      //     // If not valid HH:MM, optionally keep as-is or handle differently
                      //     formattedAppointmentTime = item.timeOfAppointment;
                      //   }
                      // }

                    return (
                     <tr key={item._id} style={analyticsGridRowStyle}>
                        <td style={{ padding: '8px', textAlign: 'center' }}>{item.clientName}</td>
                        <td style={{ padding: '8px', textAlign: 'center' }}>
                          {typeof branchData?.isMasked !== 'boolean' || !item?.clientNumber
                            ? 'loading...'
                            : maskContact(item?.clientNumber, branchData?.isMasked)}
                        </td>
                        <td style={{ padding: '8px', textAlign: 'center' }}>{formattedAppointmentDate}</td>
                        <td style={{ padding: '8px', textAlign: 'center' }}>{formattedAppointmentTime}</td>
                        <td style={{ padding: '8px', textAlign: 'center' }}>{item.gender}</td>
                        <td style={{ padding: '8px', textAlign: 'center' }}>{item.staffName}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              <p>No data available</p>
            )}
          </>
        )}
      </DashboardCard2>
    </PageContainer>
  );
};

export default AppointmentAnalytics;

